import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
    Box,
    Button,
    Container,
    Typography,
    Grid,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { alpha, keyframes } from '@mui/system';

const cyan = '#00BFFF';
const green = '#8DC63F';
const darkBg = '#060B14';
const cardBg = '#0E1626';
const cardBorder = 'rgba(0, 191, 255, 0.15)';

const fadeUp = keyframes`
    from { opacity: 0; transform: translateY(24px); }
    to { opacity: 1; transform: translateY(0); }
`;

const nextSteps = [
    'Download the companion kit.',
    'Complete Day 1 of the 7-Day Reading Reset.',
    'Try one guided ReaderFlex session using the Guide Dot.',
    'Watch your email for simple practice tips over the next few days.',
];

function EbookThankYou() {
    const downloadUrl = '/downloads/readerflex-7-day-reading-reset-companion-kit.pdf';

    return (
        <>
            <Helmet>
                <title>Your 7-Day Reading Reset Kit Is Ready | ReaderFlex</title>
                <meta name="description" content="Download your free 7-Day Reading Reset companion kit and start training your reading speed, focus, and comprehension today." />
                <meta name="robots" content="noindex" />
            </Helmet>

            <Box sx={{ backgroundColor: darkBg, color: '#fff', minHeight: '100vh', display: 'flex', alignItems: 'center' }}>
                <Container maxWidth="md" sx={{ py: { xs: 6, md: 10 } }}>
                    <Box sx={{ textAlign: 'center', mb: 6 }}>
                        <Box
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 64,
                                height: 64,
                                borderRadius: '50%',
                                backgroundColor: alpha(green, 0.1),
                                border: `1px solid ${alpha(green, 0.3)}`,
                                mb: 3,
                                animation: `${fadeUp} 0.5s ease both`,
                            }}
                        >
                            <CheckCircleIcon sx={{ fontSize: 36, color: green }} />
                        </Box>
                        <Typography
                            variant="h3"
                            component="h1"
                            sx={{
                                fontWeight: 800,
                                fontSize: { xs: '1.6rem', sm: '2.2rem', md: '2.5rem' },
                                color: '#fff',
                                letterSpacing: '-0.02em',
                                mb: 2,
                                animation: `${fadeUp} 0.5s ease 0.1s both`,
                            }}
                        >
                            Your 7-Day Reading Reset Kit Is Ready
                        </Typography>
                        <Typography
                            variant="h6"
                            sx={{
                                fontSize: { xs: '0.95rem', sm: '1.15rem' },
                                color: alpha('#fff', 0.65),
                                fontWeight: 400,
                                lineHeight: 1.6,
                                maxWidth: '500px',
                                mx: 'auto',
                                animation: `${fadeUp} 0.5s ease 0.2s both`,
                            }}
                        >
                            Download the companion kit below, then complete your first 15-minute reading reset.
                        </Typography>
                    </Box>

                    <Box
                        sx={{
                            backgroundColor: cardBg,
                            border: `1px solid ${alpha(cyan, 0.2)}`,
                            borderRadius: 4,
                            p: { xs: 4, md: 5 },
                            textAlign: 'center',
                            mb: 5,
                            animation: `${fadeUp} 0.5s ease 0.3s both`,
                        }}
                    >
                        <Button
                            component="a"
                            href={downloadUrl}
                            download
                            variant="contained"
                            size="large"
                            startIcon={<DownloadIcon />}
                            sx={{
                                backgroundColor: cyan,
                                color: '#000',
                                fontWeight: 700,
                                textTransform: 'none',
                                fontSize: '1.1rem',
                                px: 5,
                                py: 1.5,
                                borderRadius: 2,
                                boxShadow: `0 0 24px ${alpha(cyan, 0.4)}`,
                                mb: 3,
                                '&:hover': {
                                    backgroundColor: alpha(cyan, 0.85),
                                    boxShadow: `0 0 36px ${alpha(cyan, 0.6)}`,
                                },
                            }}
                        >
                            Download the Companion Kit
                        </Button>
                        <Typography
                            variant="body2"
                            sx={{ color: alpha('#fff', 0.4), fontSize: '0.85rem' }}
                        >
                            PDF download. Save it or print it.
                        </Typography>
                    </Box>

                    <Box
                        sx={{
                            backgroundColor: cardBg,
                            border: `1px solid ${cardBorder}`,
                            borderRadius: 3,
                            p: { xs: 3, md: 4 },
                            mb: 5,
                            animation: `${fadeUp} 0.5s ease 0.4s both`,
                        }}
                    >
                        <Typography
                            variant="h5"
                            sx={{
                                color: '#fff',
                                fontWeight: 700,
                                fontSize: '1.15rem',
                                mb: 3,
                            }}
                        >
                            You now have the worksheets and trackers from Read Faster Without Forgetting Everything.
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{
                                color: alpha('#fff', 0.65),
                                fontSize: '0.95rem',
                                lineHeight: 1.7,
                                mb: 3,
                            }}
                        >
                            Start with Day 1: Find Your Baseline. Keep it simple. Read for 15 minutes, check comprehension, and write one short retention note.
                        </Typography>
                        <Typography
                            variant="h6"
                            sx={{
                                color: cyan,
                                fontSize: '0.9rem',
                                fontWeight: 600,
                                textTransform: 'uppercase',
                                letterSpacing: '0.08em',
                                mb: 2,
                            }}
                        >
                            What to do next:
                        </Typography>
                        {nextSteps.map((step, i) => (
                            <Box
                                key={i}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: 2,
                                    py: 1.5,
                                    borderBottom: i < nextSteps.length - 1 ? `1px solid ${alpha('#fff', 0.06)}` : 'none',
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 24,
                                        height: 24,
                                        borderRadius: '50%',
                                        border: `1px solid ${alpha(cyan, 0.3)}`,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: cyan,
                                        fontSize: '0.75rem',
                                        fontWeight: 700,
                                        flexShrink: 0,
                                        mt: 0.2,
                                    }}
                                >
                                    {i + 1}
                                </Box>
                                <Typography sx={{ color: alpha('#fff', 0.8), fontSize: '0.9rem', lineHeight: 1.5 }}>
                                    {step}
                                </Typography>
                            </Box>
                        ))}
                    </Box>

                    <Box sx={{ textAlign: 'center', animation: `${fadeUp} 0.5s ease 0.5s both` }}>
                        <Typography
                            variant="body1"
                            sx={{ color: alpha('#fff', 0.55), fontSize: '0.95rem', mb: 3 }}
                        >
                            Ready to practice with the Guide Dot?
                        </Typography>
                        <Button
                            component={RouterLink}
                            to="/register"
                            variant="outlined"
                            size="large"
                            endIcon={<ArrowForwardIcon />}
                            sx={{
                                borderColor: cyan,
                                color: cyan,
                                fontWeight: 700,
                                textTransform: 'none',
                                fontSize: '1rem',
                                px: 4,
                                py: 1.5,
                                borderRadius: 2,
                                '&:hover': {
                                    backgroundColor: alpha(cyan, 0.08),
                                    borderColor: cyan,
                                },
                            }}
                        >
                            Open ReaderFlex and Try a Guided Session
                        </Button>
                    </Box>
                </Container>
            </Box>
        </>
    );
}

export default EbookThankYou;
